import type { ReactNode } from 'react';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { useNamespaceCopy } from '@/i18n/ns';
import { Callout } from '@/components/ui/content';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import type { ActionName, C4BlockedReason, DisplayVM } from '../artifacts/view-models';

// The seam between ART's canonical display envelope (DisplayVM<T>) and the
// panels. The panels never see the envelope: PanelGate turns its state into the
// right screen (loading, empty, denied, blocked, error) and hands `ready` data
// to the panel. Authority is not decided here: the data's own flags are already
// projected from the current actor's capabilities by ART, and a host that
// performs an action takes its handle from `authorityOf`, which returns the
// scope-bound reference only for an action the producer marked available.

export type PanelGateState<T> =
  | { kind: 'loading' }
  | { kind: 'empty'; messageKey: string }
  | { kind: 'denied'; messageKey: string }
  | { kind: 'blocked'; reasonKey: string; blockedReason: C4BlockedReason }
  | { kind: 'error'; code: string; messageKey: string }
  | { kind: 'ready'; data: T; freshness: 'current' | 'stale' | 'unknown' };

export function panelGateState<T>(vm: DisplayVM<T>): PanelGateState<T> {
  const state = vm.state;
  switch (state.status) {
    case 'loading':
      return { kind: 'loading' };
    case 'empty':
      return { kind: 'empty', messageKey: state.messageKey };
    case 'denied':
      return { kind: 'denied', messageKey: state.messageKey };
    case 'blocked':
      return { kind: 'blocked', reasonKey: state.reasonKey, blockedReason: state.blockedReason };
    case 'error':
      return { kind: 'error', code: state.code, messageKey: state.messageKey };
    case 'ready':
      return { kind: 'ready', data: state.data, freshness: state.freshness };
  }
}

/** The scope-bound authority reference of an action the producer marked available, else null. */
export function authorityOf<T>(vm: DisplayVM<T>, name: ActionName): string | null {
  const action = vm.actions.find((a) => a.name === name);
  return action && action.status === 'available' ? action.authorityRef : null;
}

export function PanelGate<T>({
  vm,
  children,
  onRetry,
}: {
  vm: DisplayVM<T>;
  /** Rendered with the panel's data when the envelope is ready. */
  children: (data: T) => ReactNode;
  onRetry?: () => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const gate = panelGateState(vm);
  switch (gate.kind) {
    case 'loading':
      return <LoadingState label={copy.panelLoading} />;
    case 'empty':
      return <EmptyState icon="files" title={copy.panelEmpty} description="" />;
    case 'denied':
      return <Callout tone="warning">{copy.panelDenied}</Callout>;
    case 'blocked':
      return (
        <Callout tone="warning">
          {gate.blockedReason === 'viewer_context_required'
            ? copy.panelBlockedViewerContext
            : copy.panelBlocked}
        </Callout>
      );
    case 'error':
      return <ErrorState message={copy.panelError} onRetry={onRetry} />;
    case 'ready':
      return (
        <>
          {gate.freshness === 'stale' && <Callout tone="warning">{copy.panelStale}</Callout>}
          {children(gate.data)}
        </>
      );
  }
}
